/* Atlas v3 — chart settings panel + the expanded "advanced" chart view.

   Two exports:
     <ChartSettingsPanel>  the gear popover — chart type + indicator toggles.
     <AdvancedChart>       full-screen chart: zoom/pan on, settings inline.

   Design notes: hairline rows and small mono labels, no icon tiles and no
   bordered card per control (v3: containment by meaning). Amber marks the
   active state; nothing here is a coloured fill.

   AdvancedChart PORTALS to document.body, and must keep doing so. It is
   position:fixed inset:0, and an ancestor with transform / filter /
   backdrop-filter becomes the containing block for fixed descendants — the
   mobile sheet panel carries `transform: translateY(...)` for its drag
   animation (TickerSheet.jsx), so rendering in place would collapse this
   overlay into the sheet. Same class of bug that made AtlasSearch unusable
   in PR #814. The portal makes the call site irrelevant. */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CandleChart, DEFAULT_INDICATORS } from './CandleChart'
import { MIN_BARS } from './indicators'

const Mono = { fontFamily: 'var(--studio-font-mono)' }

/* Icon buttons + chip pills, lifted from the studio component library
   (benjaminwolf36/studio: power-tools/toolbar and micro-interactions/
   chip-group). Same --studio-* tokens, so they drop straight in. The first
   version of this panel was a stack of full-width switch rows ~600px tall —
   it covered the entire chart on a phone, which Ben flagged as clunky. Chips
   wrap 3-4 per row and the whole panel now fits in roughly a third of that. */
const ICON = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }
const LineIcon = () => <svg {...ICON}><polyline points="3 16 8 10 12 13 16 6 21 9" /></svg>
const CandleIcon = () => (
  <svg {...ICON}><rect x="7" y="8" width="3.5" height="9" /><line x1="8.75" y1="4" x2="8.75" y2="8" /><line x1="8.75" y1="17" x2="8.75" y2="20" />
    <rect x="14" y="6" width="3.5" height="11" /><line x1="15.75" y1="3" x2="15.75" y2="6" /><line x1="15.75" y1="17" x2="15.75" y2="21" /></svg>
)
const HollowIcon = () => (
  <svg {...ICON}><rect x="7" y="8" width="3.5" height="9" fill="none" /><line x1="8.75" y1="4" x2="8.75" y2="8" /><line x1="8.75" y1="17" x2="8.75" y2="20" />
    <rect x="14" y="6" width="3.5" height="11" fill="currentColor" opacity="0.35" /><line x1="15.75" y1="3" x2="15.75" y2="6" /><line x1="15.75" y1="17" x2="15.75" y2="21" /></svg>
)
const HeikinIcon = () => (
  <svg {...ICON}><rect x="4" y="11" width="3" height="5" /><rect x="10.5" y="8" width="3" height="6" /><rect x="17" y="6" width="3" height="5" />
    <line x1="5.5" y1="9" x2="5.5" y2="11" /><line x1="12" y1="6" x2="12" y2="8" /><line x1="18.5" y1="4" x2="18.5" y2="6" /></svg>
)

export const CHART_TYPES = [
  ['line', 'Line', LineIcon],
  ['candles', 'Candles', CandleIcon],
  ['hollow', 'Hollow', HollowIcon],
  ['heikin', 'Heikin Ashi', HeikinIcon],
]

const GROUPS = [
  ['Overlays', [
    ['sma20', 'SMA 20'], ['sma50', 'SMA 50'], ['sma200', 'SMA 200'],
    ['ema20', 'EMA 20'], ['ema50', 'EMA 50'],
    ['bb', 'Bollinger'], ['vwap', 'VWAP'],
  ]],
  ['Panes', [['volume', 'Volume'], ['rsi', 'RSI'], ['macd', 'MACD']]],
]

// studio power-tools/toolbar: 28px square, amber wash when active.
function IconBtn({ active, label, onClick, children }) {
  return (
    <button onClick={onClick} title={label} aria-label={label} aria-pressed={active}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
        borderRadius: 'var(--studio-radius-sm)', border: 'none', cursor: 'pointer', flexShrink: 0,
        background: active ? 'hsl(var(--studio-accent) / 0.15)' : 'transparent',
        color: `hsl(var(--studio-${active ? 'accent' : 'text-muted'}))`,
        transition: 'background 120ms ease, color 120ms ease',
      }}>{children}</button>
  )
}

// studio micro-interactions/chip-group: rounded pill, amber outline when on.
function Chip({ on, label, need, onClick }) {
  const off = !!need
  return (
    <button onClick={off ? undefined : onClick} aria-pressed={on} aria-disabled={off}
      title={off ? `Needs ${need.min} bars — this range has ${need.have}` : label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 9px', borderRadius: 999,
        cursor: off ? 'default' : 'pointer', whiteSpace: 'nowrap',
        border: `1px solid hsl(var(--studio-${on && !off ? 'accent' : 'border'}))`,
        background: on && !off ? 'hsl(var(--studio-accent) / 0.14)' : 'transparent',
        color: off ? 'hsl(var(--studio-text-subtle) / 0.38)' : `hsl(var(--studio-${on ? 'accent' : 'text-muted'}))`,
        fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.04em',
        transition: 'all var(--studio-duration-fast, 140ms) ease',
      }}>
      {on && !off && <span style={{ fontSize: 8, lineHeight: 1 }}>✓</span>}
      {label}
    </button>
  )
}

export const SCALE_MODES = [['normal', 'Linear'], ['log', 'Log'], ['percent', '%']]
// Bar resolution, offered ONLY on the intraday ranges — 1M and longer are daily
// bars, and asking for intraday over a multi-week window is the exact request
// that came back two weeks stale on this Polygon tier (api/chart-bars.js).
export const INTERVALS = [['1m', '1m'], ['2m', '2m'], ['5m', '5m'], ['15m', '15m'], ['30m', '30m'], ['1h', '1h']]
export const INTRADAY_TF = new Set(['1D', '5D'])

export function ChartSettingsPanel({ type, setType, indicators, setIndicators, onClose, showTypes = true, barCount = Infinity, scaleMode = 'normal', setScaleMode, tf, interval, setInterval: setIv }) {
  const ind = { ...DEFAULT_INDICATORS, ...indicators }
  const flip = (k) => setIndicators({ ...ind, [k]: !ind[k] })
  const anyOn = Object.entries(ind).some(([k, v]) => v && k !== 'volume')

  return (
    <div style={{
      background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))',
      borderRadius: 'var(--studio-radius-sm)', padding: '9px 11px 8px', width: 218,
      boxShadow: '0 18px 40px hsl(0 0% 0% / 0.55)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.14em', color: 'hsl(var(--studio-accent))' }}>CHART</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {showTypes && CHART_TYPES.map(([v, label, Icon]) => (
            <IconBtn key={v} active={type === v} label={label} onClick={() => setType(v)}><Icon /></IconBtn>
          ))}
          {onClose && (
            <button onClick={onClose} aria-label="Close settings"
              style={{ all: 'unset', cursor: 'pointer', padding: '0 2px 0 6px', ...Mono, fontSize: 12, lineHeight: 1, color: 'hsl(var(--studio-text-subtle))' }}>×</button>
          )}
        </div>
      </div>

      {GROUPS.map(([title, items]) => (
        <div key={title} style={{ marginTop: 7 }}>
          <div style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 5 }}>{title.toUpperCase()}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {items.map(([k, label]) => {
              const min = MIN_BARS[k] || 1
              const need = barCount < min ? { min, have: barCount } : null
              return <Chip key={k} on={!!ind[k]} label={label} need={need} onClick={() => flip(k)} />
            })}
          </div>
        </div>
      ))}

      {setIv && INTRADAY_TF.has(tf) && (
        <div style={{ marginTop: 7 }}>
          <div style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 5 }}>INTERVAL</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {INTERVALS.map(([v, label]) => (
              <Chip key={v} on={interval === v} label={label} onClick={() => setIv(v)} />
            ))}
          </div>
        </div>
      )}

      {setScaleMode && (
        <div style={{ marginTop: 7 }}>
          {/* Log matters on the long ranges — a +6,900% MAX flattens to a line
              along the bottom in linear. */}
          <div style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 5 }}>SCALE</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {SCALE_MODES.map(([v, label]) => (
              <Chip key={v} on={scaleMode === v} label={label} onClick={() => setScaleMode(v)} />
            ))}
          </div>
        </div>
      )}

      <button onClick={() => setIndicators({ ...DEFAULT_INDICATORS })} disabled={!anyOn}
        style={{
          all: 'unset', cursor: anyOn ? 'pointer' : 'default', display: 'block', width: '100%', textAlign: 'right',
          paddingTop: 9, ...Mono, fontSize: 8, letterSpacing: '0.12em',
          color: anyOn ? 'hsl(var(--studio-text-subtle))' : 'hsl(var(--studio-text-subtle) / 0.3)',
        }}>RESET</button>
    </div>
  )
}

/* The advanced chart, as a bottom SHEET (mirrors NewsSheet / PickSheet motion)
   rather than a full-screen takeover. The takeover ran under the iOS status
   bar — the ticker printed over the clock and the close button over the
   battery — and gave the chart no visual frame. Capping the panel at 92%
   leaves the status bar on the backdrop, and the drag handle + tap-outside
   match every other sheet in the app.

   Still portalled to document.body: it is position:fixed, and the mobile
   TickerSheet panel carries `transform: translateY(...)` for its drag
   animation. A transformed ancestor becomes the containing block for fixed
   descendants, which would trap this inside the sheet — the same class of bug
   that made AtlasSearch unusable in PR #814. */
export function AdvancedChart({
  ticker, bars, warmup = null, tf, setTf, timeframes, type, setType, indicators, setIndicators, up, onClose, desktop = false,
  scaleMode = 'normal', setScaleMode, interval, setInterval: setIv,
}) {
  const [shown, setShown] = useState(false)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [onClose])

  const indSrc = (warmup && bars && warmup.length > bars.length) ? warmup : bars
  const barCount = Array.isArray(indSrc) ? indSrc.filter((b) => b && b.c != null).length : 0

  const panel = desktop
    ? { position: 'relative', width: 'min(860px, 94vw)', maxHeight: '88vh', margin: '5vh auto 0', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', border: '1px solid hsl(var(--studio-border))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)',
        borderRadius: 'var(--studio-radius-lg)', boxShadow: '0 32px 80px -24px hsl(var(--studio-overlay) / 0.9)',
        opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 220ms ease, transform 220ms var(--studio-ease)', overflow: 'hidden' }
    : { position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '92%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.55)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }

  // z-index 190 matches the Insights reader sheet: above the V3 desktop
  // right dock (an <aside> at 150, which at 120 painted over this modal's
  // right edge and clipped the chart's price axis) and below search / the
  // command palette at 200.
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 190 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={panel}>
        <div style={{ padding: desktop ? '16px 20px 0' : '9px 18px 0', flexShrink: 0 }}>
          {!desktop && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
              <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
              <span style={{ fontFamily: 'var(--studio-font-display)', fontSize: 19, color: 'hsl(var(--studio-text))' }}>{ticker}</span>
              <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.16em', color: 'hsl(var(--studio-accent))' }}>ADVANCED</span>
            </div>
            <button onClick={onClose} aria-label="Close advanced chart"
              style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 13 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 13, padding: '10px 0 8px', overflowX: 'auto' }}>
            {timeframes.map((x) => (
              <button key={x} onClick={() => setTf(x)} style={{
                all: 'unset', cursor: 'pointer', ...Mono, fontSize: 11, letterSpacing: '0.06em', padding: '2px 0', flexShrink: 0,
                color: `hsl(var(--studio-${tf === x ? 'accent-soft' : 'text-subtle'}))`,
                borderBottom: `1.5px solid ${tf === x ? 'hsl(var(--studio-accent))' : 'transparent'}`,
              }}>{x}</button>
            ))}
          </div>
        </div>

        {/* pb accounts for the iOS home indicator on the sheet's bottom edge */}
        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div style={{ padding: '2px 6px 12px' }}>
            <CandleChart bars={bars} warmup={warmup} type={type} up={up} height={desktop ? 300 : 260} tf={tf} indicators={indicators} scaleMode={scaleMode} zoom />
          </div>
          <div style={{ padding: '0 18px 26px' }}>
            <ChartSettingsPanel type={type} setType={setType} indicators={indicators} setIndicators={setIndicators} barCount={barCount}
            scaleMode={scaleMode} setScaleMode={setScaleMode} tf={tf} interval={interval} setInterval={setIv} />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
